import * as React from "react";
import { SearchBar } from "../SearchBar";
import { useLocationConfiguration } from "../LocationConfiguration";
import { useSyndeticsConfig } from "../SyndeticsConfig";
import SearchBarContainer from "../SearchBarContainer";
import { useNavigate } from "react-router-dom";
import { useSearchSubmit } from "./search/useSearchSubmit";
import axios from "axios";
import { LandingPageConfigurationView } from "@tlc/ls2pac/dto/LandingPageConfiguration";
import { createAsyncCache } from "../../util/createAsyncCache";
import { useCallback, useState } from "react";
import { ResourceListView } from "@tlc/ls2pac/dto/ResourceList";
import { CarouselTabs } from "../CarouselTabs";
import { BriefSearchResultView } from "@tlc/ls2pac/dto/BriefSearchResultView";
import { createKeyedAsyncCache } from "../../util/createKeyedAsyncCache";
import { Slider } from "src/main/resources/javascript/tlc/carousel/slider";
import { useAnalytics } from "../../analytics/useGoogleAnalytics";

async function getLandingPageConfiguration() {
  const response = await axios.get<LandingPageConfigurationView>(
    "/landingPageConfiguration",
  );
  return response.data;
}

const landingPageConfigurationCache = createAsyncCache(
  getLandingPageConfiguration,
);

const landingPageListCache = createKeyedAsyncCache<
  ResourceListView["url"],
  BriefSearchResultView
>(async (listUrl: string) => {
  const { data } = await axios.get<BriefSearchResultView>(listUrl);
  return data;
});

async function getLandingPageLists(lists: ResourceListView[]) {
  const requests = lists.map((list) => landingPageListCache.get(list.url));
  return await Promise.all(requests);
}

interface SliderList extends ResourceListView {
  listItems: BriefSearchResultView["resources"];
}

interface SliderConfig {
  target: string; // DOM element
  lists: SliderList[];
  handleAutoAdvanceToNextList: (newIndex: number) => void;
  isDebugEnabled?: boolean;
  slideTime?: number;
  customIsActiveView?: () => boolean;
  trackLandingPageNavigate: (direction: "next" | "prev") => void;
  trackLandingPageJacketClick: () => void;
}
interface SliderI {
  destroy: () => void;
  setDefaultResourceList: (
    listId: ResourceListView["id"],
    listItems: BriefSearchResultView["resources"],
  ) => void;
  setResourceList: (
    listId: ResourceListView["id"],
    listItems: BriefSearchResultView["resources"],
  ) => void;
  switchLists: (newIndex: number) => void;
}

export const HomePage: React.FC = function () {
  const { locationConfiguration } = useLocationConfiguration();
  const { syndeticsConfig } = useSyndeticsConfig();
  const {
    trackLandingPageNavigate,
    trackLandingPageListChange,
    trackLandingPageJacketClick,
  } = useAnalytics();
  const navigate = useNavigate();
  const searchSubmit = useSearchSubmit();
  const [lists, setLists] = useState<ResourceListView[]>(
    landingPageConfigurationCache.current?.resourceLists || [],
  );
  const [selectedListIndex, setSelectedListIndex] = useState<number>(0);
  const sliderRef = React.useRef<SliderI | null>(null);

  const handleAutoAdvanceToNextList = useCallback((newIndex: number) => {
    setSelectedListIndex(newIndex);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    let slider: SliderI | null = null;
    landingPageConfigurationCache
      .get()
      .then((config) => {
        if (cancelled) return;
        if (config?.resourceLists?.length > 0) {
          getLandingPageLists(config.resourceLists).then((lists) => {
            if (cancelled) return;
            const listsWithHits = lists
              .map((list, index) => {
                const filteredResources = syndeticsConfig?.active
                  ? list.resources
                  : list.resources.filter(
                      ({ localLargeImage }) => !!localLargeImage,
                    );
                // IMPORTANT: Create a copy of the items to avoid mutation of the cached data in legacy code
                return {
                  ...config.resourceLists[index],
                  listItems: filteredResources.map((resource) => ({
                    ...resource,
                  })),
                };
              })
              .filter((list) => {
                if (list.listItems.length === 0) {
                  console.warn(
                    `Resource list with id ${list.id} has no hits and will be filtered out`,
                  );
                  return false;
                }

                return true;
              });
            if (listsWithHits.length === 0) {
              console.warn(
                "No resource lists have hits, slider will not be rendered",
              );
              return;
            }
            setLists(listsWithHits);
            const sliderConfig: SliderConfig = {
              target: "#homePageContainer",
              lists: listsWithHits,
              handleAutoAdvanceToNextList,
              trackLandingPageNavigate,
              trackLandingPageJacketClick,
            };
            slider = new Slider(sliderConfig);
            sliderRef.current = slider;
            listsWithHits.forEach((list, index) => {
              if (index === 0) {
                slider.setDefaultResourceList(
                  list.id,
                  list.listItems.map((resource) => ({ ...resource })),
                ); // create copy of resources to avoid mutation of cached data in legacy code
              } else {
                slider.setResourceList(
                  list.id,
                  list.listItems.map((resource) => ({ ...resource })),
                ); // create copy of resources to avoid mutation of cached data in legacy code
              }
            });
          });
        } else {
          console.warn("Landing page configuration has no resource lists");
        }
      })
      .catch((error) => {
        console.error("Error fetching landing page configuration:", error);
      });

    return () => {
      cancelled = true;
      slider?.destroy();
      if (sliderRef.current === slider) {
        sliderRef.current = null;
      }
    };
  }, [
    handleAutoAdvanceToNextList,
    syndeticsConfig?.active,
    trackLandingPageNavigate,
  ]);

  return (
    <>
      <SearchBarContainer>
        <SearchBar
          sx={{ m: "0 auto" }}
          limitToKids={false}
          voiceSearchEnabled={
            locationConfiguration?.pacSettings?.displaySettings?.voiceInterface
          }
          onClickSearch={searchSubmit}
          onClickTitle={(resource) => {
            navigate(`/resource/${resource.id}`);
          }}
          syndeticsClientCode={syndeticsConfig?.clientCode}
          branchFilters={locationConfiguration?.filterBranches}
        ></SearchBar>
      </SearchBarContainer>
      <CarouselTabs
        names={lists.map((list) => list.name)}
        selectedIndex={selectedListIndex}
        onChange={(newIndex: number) => {
          trackLandingPageListChange(
            lists[newIndex]?.name,
            lists[selectedListIndex]?.name,
          );
          setSelectedListIndex(newIndex);
          sliderRef.current?.switchLists(newIndex);
        }}
      />
      <div id="homePageContainer"></div>
    </>
  );
};
